import { ReloadOutlined } from '@ant-design/icons';
import { captureException } from '@sentry/react';
import { useRequest } from 'ahooks';
import { Button, Result } from 'antd';
import PropTypes from 'prop-types';
import { createContext, FC, useContext, useEffect, useState } from 'react';

import { useDatagonUrl } from '../DatagonProvider';
import { useEnvironment } from '../EnvironmentProvider';
import Environment from '../EnvironmentProvider/Environment';
import Loading from '../Loading';

type Config = {
  geonamesUrl: string;
  datagonUrl: string;
  environment: Environment;
  intercomId: string;
  legacy: boolean;
};

type Configs = Record<Environment, Config>;

export const ConfigContext = createContext<Configs | undefined>(undefined);

export const useConfigs = (): Configs => {
  const configs = useContext(ConfigContext);

  if (!configs) {
    throw new Error('No config');
  }

  return configs;
};

export const useIntendedConfig = (): Config => {
  const configs = useConfigs();

  return Object.entries(configs)[0][1];
};

export const useConfig = (): Config => {
  const configs = useConfigs();
  const [environment] = useEnvironment();
  const intendedConfig = useIntendedConfig();
  const config = environment && configs[environment];

  return config || intendedConfig;
};

const ConfigProvider: FC = ({ children }) => {
  const datagonUrl = useDatagonUrl();
  const [environment, setEnvironment] = useEnvironment();
  const [configs, setConfigs] = useState<Configs>();

  const { data: config, error, loading, run } = useRequest<Config>(
    `/${environment || 'config'}.json?cache=${CACHE}`,
    {
      onError: (error) => {
        captureException(error);
      },
      refreshDeps: [environment],
    },
  );

  useEffect(() => {
    // Load config if it wasn't already loaded before
    if (environment && configs && !configs[environment]) {
      void run(environment);
    }
  }, [environment, run, configs]);

  useEffect(() => {
    // Need to initialize environment based on config.json
    if (!environment && config) {
      setEnvironment(config.environment);
    }
  }, [config, environment, setEnvironment]);

  useEffect(() => {
    if (config) {
      setConfigs(
        (prevState) =>
          ({
            ...prevState,
            [config.environment]: config.legacy
              ? { ...config, datagonUrl }
              : config,
          } as Configs), // https://github.com/DefinitelyTyped/DefinitelyTyped/issues/26635)
      );
    }
  }, [config, datagonUrl, setEnvironment]);

  if (error) {
    return (
      <Result
        extra={
          <Button
            href={window.location.href}
            icon={<ReloadOutlined />}
            type="primary"
          >
            Reload
          </Button>
        }
        status={500}
        subTitle="Error during config fetch"
        title="Error"
      />
    );
  }

  if ((loading && !configs) || !configs) {
    return <Loading />;
  }

  return (
    <ConfigContext.Provider value={configs}>{children}</ConfigContext.Provider>
  );
};

ConfigProvider.propTypes = {
  children: PropTypes.node,
};

ConfigProvider.displayName = 'ConfigProvider';

export default ConfigProvider;
